import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Edit3, Download, Eye, Shield, CheckCircle, AlertTriangle, DollarSign, Calendar, ChevronRight, Save, Printer } from 'lucide-react';

const REPORT_SECTIONS = [
  {
    id: 'summary',
    title: 'Executive Summary',
    editable: true,
    content: 'This compliance report has been generated by SMITHERS XI, the proprietary content protection and enforcement platform operated under Quadra Core. The data herein represents verified scan results, identified infringements, and recommended enforcement actions as of the report generation date. All figures are estimated based on available data and should be independently verified before legal proceedings.',
  },
  {
    id: 'scan_results',
    title: 'Scan Results Overview',
    editable: false,
    content: null,
    data: {
      totalScanned: '24 platforms',
      violationsDetected: 8,
      matchAccuracy: '94.2%',
      estimatedRevenue: '$14,500',
      scanDate: new Date().toISOString().split('T')[0],
    }
  },
  {
    id: 'violations',
    title: 'Identified Infringements',
    editable: false,
    content: null,
    violations: [
      { platform: 'Instagram', type: 'Image repost', match: '97%', revenue: '$340', status: 'Confirmed' },
      { platform: 'Twitter/X', type: 'Video embed', match: '94%', revenue: '$520', status: 'Confirmed' },
      { platform: 'TikTok', type: 'Video repost', match: '91%', revenue: '$1,200', status: 'Under Review' },
      { platform: 'Telegram', type: 'Bulk distribution', match: '89%', revenue: '$3,400', status: 'Confirmed' },
      { platform: 'External Site', type: 'Full mirror', match: '99%', revenue: '$4,200', status: 'Confirmed' },
    ]
  },
  {
    id: 'legal',
    title: 'Legal Framework & Compliance',
    editable: true,
    content: 'Enforcement actions are conducted under the Digital Millennium Copyright Act (DMCA), 17 U.S.C. § 512. All takedown notices comply with the statutory requirements including: (1) identification of the copyrighted work, (2) identification of the infringing material, (3) good faith statement that the material is unauthorized, and (4) accuracy statement under penalty of perjury. Settlement offers are calculated based on documented ad revenue, engagement metrics, and comparable licensing rates.',
  },
  {
    id: 'recommendations',
    title: 'Recommended Actions',
    editable: true,
    content: '1. Issue DMCA takedown notices to all confirmed infringers.\n2. Initiate settlement negotiations with high-value targets ($1,000+ estimated revenue).\n3. Escalate bulk distribution cases (Telegram, external sites) to legal counsel.\n4. Register additional content with .VIG Protocol for enhanced future protection.\n5. Increase monitoring frequency on TikTok and Instagram due to elevated infringement rates.',
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer & Compliance Notice',
    editable: true,
    content: 'IMPORTANT: All revenue estimates are approximations based on publicly available data including view counts, engagement metrics, and industry-standard CPM rates. These figures should not be construed as guaranteed recoverable amounts. Actual settlement and recovery amounts may vary. This report does not constitute legal advice. Consult with qualified legal counsel before initiating any enforcement action. SMITHERS XI and Quadra Core assume no liability for actions taken based on this report.',
  },
];

export default function PreviewDocPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sections, setSections] = useState(REPORT_SECTIONS);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [complianceApproved, setComplianceApproved] = useState(false);
  const [showComplianceWarning, setShowComplianceWarning] = useState(false);
  const printRef = useRef(null);

  const startEdit = (section) => {
    setEditingId(section.id);
    setEditContent(section.content);
    setComplianceApproved(false); // Reset compliance when editing
  };

  const saveEdit = () => {
    setSections(prev => prev.map(s =>
      s.id === editingId ? { ...s, content: editContent } : s
    ));
    setEditingId(null);
    setEditContent('');
  };

  const handleDownload = () => {
    if (!complianceApproved) {
      setShowComplianceWarning(true);
      return;
    }
    // Generate downloadable text report
    let text = `SMITHERS XI — COMPLIANCE REPORT\nGenerated: ${new Date().toLocaleString()}\n${'='.repeat(60)}\n\n`;
    sections.forEach(s => {
      text += `## ${s.title}\n`;
      if (s.content) text += `${s.content}\n\n`;
      if (s.data) {
        Object.entries(s.data).forEach(([k, v]) => {
          text += `  ${k}: ${v}\n`;
        });
        text += '\n';
      }
      if (s.violations) {
        s.violations.forEach((v, i) => {
          text += `  ${i+1}. ${v.platform} — ${v.type} (${v.match} match) — Est. ${v.revenue} — ${v.status}\n`;
        });
        text += '\n';
      }
    });
    text += `\n${'='.repeat(60)}\nCompliance Approved: YES\nApproved By: ${user?.username || user?.email}\nDate: ${new Date().toLocaleString()}\n`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `smithers-xi-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-texture" data-testid="preview-doc-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>
            <FileText size={14} className="text-[--ch-purple]" />
            <span className="font-label text-[--ch-purple]">COMPLIANCE DOCUMENT</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black" data-testid="preview-doc-title">
            <span className="text-white">Preview </span><span className="neon-purple">Report</span>
          </h1>
          <p className="text-sm text-[--ch-text-secondary] mt-1">
            Editable compliance document. All changes must pass compliance review before distribution.
          </p>
        </div>

        {/* Report Document */}
        <div ref={printRef} className="ch-card overflow-hidden animate-fade-in-up stagger-1" data-testid="report-document">
          {/* Document header */}
          <div className="p-6 border-b border-[--ch-border]"
            style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.05), rgba(0,240,255,0.02))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-black text-lg" style={{ fontFamily: 'Orbitron' }}>
                  <span className="text-[--ch-cyan]">SMITHERS</span> <span className="text-[--ch-magenta]">XI</span>
                </h2>
                <p className="text-xs text-[--ch-text-secondary]">Compliance & Enforcement Report</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-[--ch-text-secondary]">Report Date</p>
                <p className="text-sm font-bold">{new Date().toLocaleDateString()}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Prepared For', value: user?.username || 'Creator' },
                { label: 'Report ID', value: `SXI-RPT-${Date.now().toString(36).toUpperCase().slice(-6)}` },
                { label: 'Classification', value: 'CONFIDENTIAL' },
                { label: 'Status', value: complianceApproved ? 'APPROVED' : 'DRAFT' },
              ].map((f, i) => (
                <div key={i} className="p-2 rounded" style={{ background: 'rgba(0,0,0,0.3)' }}>
                  <p className="text-[9px] font-label text-[--ch-text-secondary]">{f.label}</p>
                  <p className="text-xs font-bold">{f.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sections */}
          {sections.map((section, idx) => (
            <div key={section.id} className="border-b border-[--ch-border] last:border-0" data-testid={`report-section-${section.id}`}>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
                    <span className="text-[--ch-text-secondary] text-xs">{String(idx + 1).padStart(2, '0')}.</span>
                    {section.title}
                  </h3>
                  {section.editable && editingId !== section.id && (
                    <button onClick={() => startEdit(section)}
                      data-testid={`edit-${section.id}`}
                      className="text-xs text-[--ch-purple] hover:text-white flex items-center gap-1 transition-colors">
                      <Edit3 size={12} /> Edit
                    </button>
                  )}
                </div>

                {/* Editable content */}
                {editingId === section.id ? (
                  <div>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={6}
                      className="w-full p-3 rounded-lg bg-[--ch-bg-secondary] border border-[--ch-purple] text-sm text-white resize-y focus:outline-none"
                      data-testid={`edit-textarea-${section.id}`}
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={saveEdit}
                        className="px-4 py-2 rounded-lg text-xs font-bold bg-[rgba(139,92,246,0.15)] text-[--ch-purple] border border-[--ch-purple] flex items-center gap-1">
                        <Save size={12} /> Save Changes
                      </button>
                      <button onClick={() => setEditingId(null)}
                        className="px-4 py-2 rounded-lg text-xs text-[--ch-text-secondary] hover:text-white transition-colors">
                        Cancel
                      </button>
                      <div className="flex-1" />
                      <div className="flex items-center gap-1">
                        <AlertTriangle size={12} className="text-[--ch-warning]" />
                        <span className="text-[10px] text-[--ch-warning]">Changes require compliance approval</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {section.content && (
                      <p className="text-sm text-[--ch-text-secondary] leading-relaxed whitespace-pre-line">{section.content}</p>
                    )}
                    {section.data && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                        {Object.entries(section.data).map(([key, val]) => (
                          <div key={key} className="p-3 rounded-lg" style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }}>
                            <p className="text-[9px] font-label text-[--ch-text-secondary] capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </p>
                            <p className="text-sm font-bold text-white">{val}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {section.violations && (
                      <div className="mt-2 space-y-1.5">
                        {section.violations.map((v, i) => (
                          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg"
                            style={{ background: 'rgba(20,20,20,0.5)', border: '1px solid #1A1A1A' }}>
                            <span className="text-xs text-[--ch-text-secondary] w-5">{i + 1}.</span>
                            <span className="text-xs font-bold flex-1">{v.platform}</span>
                            <span className="text-[10px] text-[--ch-text-secondary]">{v.type}</span>
                            <span className="text-[10px] text-[--ch-cyan] font-bold">{v.match}</span>
                            <span className="text-[10px] text-[--ch-warning] font-bold">{v.revenue}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                              v.status === 'Confirmed' ? 'bg-[rgba(34,197,94,0.1)] text-[--ch-success]' : 'bg-[rgba(245,158,11,0.1)] text-[--ch-warning]'
                            }`}>
                              {v.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Compliance Approval & Download */}
        <div className="ch-card p-6 mt-6 animate-fade-in-up stagger-2" data-testid="compliance-approval">
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Orbitron' }}>
            <Shield size={14} className="text-[--ch-purple]" /> Compliance Approval
          </h3>

          <label className="flex items-start gap-3 cursor-pointer mb-4 p-3 rounded-lg hover:bg-[rgba(139,92,246,0.03)] transition-colors"
            data-testid="compliance-checkbox-label">
            <input type="checkbox" checked={complianceApproved} onChange={(e) => { setComplianceApproved(e.target.checked); setShowComplianceWarning(false); }}
              className="mt-0.5 accent-[--ch-purple]"
              data-testid="compliance-checkbox" />
            <div>
              <p className="text-sm font-semibold">I confirm this report is accurate and approved for distribution</p>
              <p className="text-xs text-[--ch-text-secondary] mt-0.5">
                By checking this box, you acknowledge that all data has been reviewed, revenue estimates are approximate,
                and the report complies with all applicable regulations. Unauthorized modifications may void legal protections.
              </p>
            </div>
          </label>

          {showComplianceWarning && (
            <div className="p-3 rounded-lg mb-4 flex items-center gap-2"
              style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}
              data-testid="compliance-warning">
              <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
              <p className="text-xs text-red-400">You must approve compliance before downloading the report.</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={handleDownload}
              data-testid="download-report-btn"
              className={`px-6 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 flex-1 transition-all ${
                complianceApproved
                  ? 'btn-neon'
                  : 'bg-[--ch-bg-secondary] text-[--ch-text-secondary] border border-[--ch-border] cursor-not-allowed'
              }`}>
              <Download size={14} /> Download Report
            </button>
            <button onClick={() => navigate('/enforcement')}
              className="btn-outline-neon px-6 py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 flex-1"
              style={{ borderColor: '#8B5CF6', color: '#8B5CF6' }}>
              <Eye size={14} /> View Enforcement <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
